import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2, Play, ArrowLeft, Video as VideoIcon } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import ReelPlayer from "../components/reels/ReelPlayer";
import ReelTabs from "../components/reels/ReelTabs";
import EnquireSheet from "../components/reels/EnquireSheet";
import ReportReelSheet from "../components/reels/ReportReelSheet";
import ShareReelSheet from "../components/reels/ShareReelSheet";
import CommentSheet from "../components/reels/CommentSheet";
import { setActiveReel, stopAllReels, setMuted as setControllerMuted } from "../components/reels/reelsController";

/** GPM REELS — full-screen vertical short-video feed. */
export default function ReelsFeed() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { reelId } = useParams(); // when opened via /reels/:id

    const [tab, setTab]         = useState("for_you");
    const [city, setCity]       = useState(null);   // Public feed by default — no user-city filter
    const [category, setCategory] = useState(null);
    const [items, setItems]     = useState(null);   // null = never fetched; [] = fetched empty
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState(null);
    const [muted, setMuted]     = useState(false);  // Attempt autoplay WITH audio; fallback to muted silently
    const [active, setActive]   = useState(0);
    const [enquireReel, setEnquireReel] = useState(null);
    const [reportReel, setReportReel]   = useState(null);
    const [shareReel, setShareReel]     = useState(null);
    const [commentReel, setCommentReel] = useState(null);

    const containerRef = useRef(null);

    const params = useMemo(() => ({ tab, city, category }), [tab, city, category]);

    // ---- load feed
    useEffect(() => {
        let cancel = false;
        const load = async () => {
            setLoading(true);
            setErrorMsg(null);
            try {
                if (reelId) {
                    const one = await api.get(`/reels/${reelId}`).then((r) => r.data?.reel).catch(() => null);
                    const list = await api.get("/reels/feed", { params: { tab: "for_you", limit: 15 } }).then((r) => r.data?.items || []);
                    if (!cancel) {
                        setItems(one ? [one, ...list.filter((x) => x.id !== one.id)] : list);
                        setActive(0);
                    }
                } else {
                    const res = await api.get("/reels/feed", { params }).then((r) => r.data?.items || []);
                    if (!cancel) {
                        setItems(res);
                        setActive(0);
                    }
                }
            } catch (err) {
                if (!cancel) {
                    setErrorMsg("Couldn't load Reels. Please try again.");
                    setItems([]);
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        };
        load();
        return () => { cancel = true; };
    }, [tab, city, category, reelId, params]);

    // ---- IntersectionObserver → track active reel
    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        const io = new IntersectionObserver(
            (entries) => {
                // Only the MOST visible slide (>=0.7 intersection) becomes active.
                // Pick the entry with the highest intersectionRatio to avoid two-active flicker mid-swipe.
                let best = null;
                entries.forEach((e) => {
                    if (e.isIntersecting && e.intersectionRatio >= 0.7) {
                        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
                    }
                });
                if (best) {
                    const idx = Number(best.target.getAttribute("data-idx"));
                    setActive(idx);
                }
            },
            { root, threshold: [0.5, 0.7, 0.9, 1.0] }
        );
        root.querySelectorAll("[data-reel-slide]").forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [items]);

    // ---- Drive the central playback controller — enforces single-active playback
    useEffect(() => {
        if (!items || !items.length) return;
        const r = items[active];
        if (!r) return;
        setActiveReel(r.id);
    }, [active, items]);

    // ---- Reflect the mute preference into the controller
    useEffect(() => { setControllerMuted(muted); }, [muted]);

    // ---- Full cleanup on unmount (leaving /reels)
    useEffect(() => {
        return () => { stopAllReels(); };
    }, []);

    // ---- record view when reel becomes active
    useEffect(() => {
        if (!items || !items.length) return;
        const r = items[active];
        if (!r) return;
        api.post(`/reels/${r.id}/view`).catch(() => {});
    }, [active, items]);

    const handleLike = useCallback(async (reel) => {
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
            return;
        }
        try {
            const res = await api.post(`/reels/${reel.id}/like`).then((r) => r.data);
            setItems((list) => (list || []).map((x) => x.id === reel.id
                ? { ...x, liked: res.liked, likes_count: (x.likes_count || 0) + (res.liked ? 1 : -1) }
                : x));
        } catch { toast.error("Could not like this reel"); }
    }, [user, navigate, location.pathname]);

    const handleSave = useCallback(async (reel) => {
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
            return;
        }
        try {
            const res = await api.post(`/reels/${reel.id}/save`).then((r) => r.data);
            setItems((list) => (list || []).map((x) => x.id === reel.id
                ? { ...x, saved: res.saved, saves_count: (x.saves_count || 0) + (res.saved ? 1 : -1) }
                : x));
            toast.success(res.saved ? "Saved" : "Removed from saved");
        } catch { toast.error("Could not save this reel"); }
    }, [user, navigate, location.pathname]);

    // Loading state (also shown while items === null before first fetch completes)
    if (loading || items === null) {
        return (
            <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center gap-2" data-testid="reels-loading">
                <Loader2 className="w-7 h-7 animate-spin" style={{ color: "#F59E0B" }} />
                <div className="font-heading font-extrabold text-[13px]" style={{ color: "#002657" }}>Loading Reels…</div>
            </div>
        );
    }

    // API error → retry
    if (errorMsg) {
        return (
            <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="reels-error">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "#FEE2E2" }}>
                    <VideoIcon className="w-6 h-6" style={{ color: "#B91C1C" }} />
                </div>
                <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>{errorMsg}</div>
                <button
                    onClick={() => { setItems(null); setErrorMsg(null); }}
                    data-testid="reels-retry"
                    className="px-4 py-2 rounded-xl text-[12px] font-extrabold shadow"
                    style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="reels-empty" style={{ color: "#002657" }}>
                {/* Simple light header */}
                <div className="flex items-center gap-2 px-3 pt-3 pb-3 border-b" style={{ borderColor: "rgba(0,38,87,0.08)" }}>
                    <button aria-label="Back" onClick={() => navigate("/")} data-testid="reels-close"
                            className="w-9 h-9 rounded-full flex items-center justify-center"
                            style={{ background: "#f6f7fb" }}>
                        <ArrowLeft className="w-5 h-5" style={{ color: "#002657" }} />
                    </button>
                    <div className="flex-1 flex items-center gap-1.5 min-w-0">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                             style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)" }}>
                            <VideoIcon className="w-3.5 h-3.5" style={{ color: "#001a3d" }} />
                        </div>
                        <div className="font-heading font-extrabold text-[14px]">Property Reels</div>
                    </div>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                         style={{ background: "#FBF6E0" }}>
                        <Play className="w-6 h-6" style={{ color: "#F59E0B" }} />
                    </div>
                    <div className="font-heading font-extrabold text-[15px]">No Reels here yet</div>
                    <div className="text-[12px] mt-1" style={{ color: "rgba(0,38,87,0.6)" }}>
                        Try changing the city or category filter, or be the first to publish one.
                    </div>
                    <button
                        onClick={() => navigate("/reels/create")}
                        data-testid="reels-empty-create"
                        className="mt-4 px-4 py-2 rounded-xl text-[12px] font-extrabold shadow"
                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                    >
                        Create a Reel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-black" data-testid="reels-feed">
            <ReelTabs
                tab={tab} setTab={setTab}
                city={city} setCity={setCity}
                category={category} setCategory={setCategory}
                onClose={() => navigate("/")}
            />
            <div
                ref={containerRef}
                className="h-full w-full overflow-y-scroll snap-y snap-mandatory scroll-smooth"
                style={{ scrollbarWidth: "none" }}
                data-testid="reels-scroller"
            >
                {items.map((r, idx) => (
                    <div
                        key={r.id}
                        data-reel-slide
                        data-idx={idx}
                        className="h-full w-full snap-start snap-always"
                    >
                        <ReelPlayer
                            reel={r}
                            active={idx === active}
                            muted={muted}
                            onToggleMute={() => setMuted((m) => !m)}
                            onLike={() => handleLike(r)}
                            onSave={() => handleSave(r)}
                            onShare={() => setShareReel(r)}
                            onEnquire={() => setEnquireReel(r)}
                            onComment={() => setCommentReel(r)}
                            onReport={() => {
                                if (!user) return navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
                                setReportReel(r);
                            }}
                        />
                    </div>
                ))}
            </div>

            {enquireReel && (
                <EnquireSheet
                    reel={enquireReel}
                    onClose={() => setEnquireReel(null)}
                />
            )}
            {reportReel && (
                <ReportReelSheet
                    reel={reportReel}
                    onClose={() => setReportReel(null)}
                />
            )}
            {shareReel && (
                <ShareReelSheet
                    reel={shareReel}
                    onClose={() => setShareReel(null)}
                />
            )}
            {commentReel && (
                <CommentSheet
                    reel={commentReel}
                    onClose={() => setCommentReel(null)}
                    onCount={(delta) => setItems((list) => list.map((x) =>
                        x.id === commentReel.id
                            ? { ...x, comments_count: Math.max(0, (x.comments_count || 0) + delta) }
                            : x
                    ))}
                />
            )}
        </div>
    );
}
